/**
 * The hosted public service request form (tracker item 49): /request-service/:key, outside the app's
 * auth and nav. A company links to it from its website. The key alone
 * identifies the company; an unusable key (unknown, replaced, form off) is "not available". The page
 * shows nothing about the company: the API returns only the offered services (id and name).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createAPIClient, IRequestsApi } from '../../api';
import * as types from '../../api/types';
import { inputClass, labelClass, primaryButtonClass } from '../../components/formStyles';
import axios from 'axios';
import { getErrorCode, getErrorMessage } from '../../utils/apiError';
import { EMPTY_WEB_FORM, WebFormFields, webFormBody, webFormProblems } from '../../utils/webForm';

const getErrorStatus = (cause: unknown): number | undefined => (axios.isAxiosError(cause) ? cause.response?.status : undefined);

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function submitErrorMessage(cause: unknown): string {
  const status = getErrorStatus(cause);
  if (status === 429) return 'Too many requests were sent from here. Please wait a while and try again, or call us.';
  if (getErrorCode(cause) === 'TOO_MANY_OPEN_REQUESTS') return 'We cannot take more online requests right now. Please call us instead.';
  if (status === 404) return 'This form is no longer available.';
  if (status === 413) return 'The request is too long. Please shorten the notes.';
  return getErrorMessage(cause, 'Your request could not be sent. Please try again or call us.');
}

export function PublicRequestServicePage() {
  const { key = '' } = useParams<{ key: string }>();
  const api = useMemo<IRequestsApi>(() => createAPIClient(), []);
  const [items, setItems] = useState<types.WebFormItem[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [form, setForm] = useState<WebFormFields>(EMPTY_WEB_FORM);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Request service';
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    let active = true;
    api.getWebFormItems(key)
      .then((list) => { if (active) setItems(list); })
      .catch(() => { if (active) setUnavailable(true); });
    return () => { active = false; };
  }, [api, key]);

  const today = localToday();
  const problems = webFormProblems(form, today);
  const shown = touched ? problems : {};
  const set = (change: Partial<WebFormFields>) => { setForm({ ...form, ...change }); setError(null); };

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (Object.keys(problems).length > 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.submitWebFormRequest(key, webFormBody(form));
      setSent(true);
    } catch (cause) {
      setError(submitErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-gray-100 px-4 py-6 sm:py-10">
      <main className="mx-auto max-w-xl rounded-lg bg-white p-5 shadow sm:p-8">{children}</main>
    </div>
  );

  if (unavailable) {
    return shell(
      <>
        <h1 className="text-xl font-bold text-gray-900">Request service</h1>
        <p role="alert" className="mt-3 text-gray-700">This form is not available. Please contact the company directly.</p>
      </>
    );
  }
  if (!items) return shell(<p role="status">Loading…</p>);
  if (sent) {
    return shell(
      <>
        <h1 className="text-xl font-bold text-gray-900">Thank you</h1>
        <p role="status" className="mt-3 text-gray-700">We received your request and will contact you soon.</p>
        <button type="button" className="mt-4 min-h-[44px] text-sm font-semibold text-blue-700 underline" onClick={() => { setForm(EMPTY_WEB_FORM); setTouched(false); setSent(false); }}>
          Send another request
        </button>
      </>
    );
  }

  const fieldError = (field: keyof typeof shown) =>
    shown[field] ? <p id={`wf-${field}-error`} className="mt-1 text-xs text-red-700">{shown[field]}</p> : null;

  return shell(
    <>
      <h1 className="text-xl font-bold text-gray-900">Request service</h1>
      <p className="mt-1 text-sm text-gray-600">Tell us what you need and how to reach you. We will reply to confirm a date.</p>
      <form className="mt-5 space-y-4" onSubmit={(e) => void submit(e)} noValidate aria-label="Request service">
        <div>
          <label htmlFor="wf-name" className={labelClass}>Your name</label>
          <input id="wf-name" className={`${inputClass} min-h-[44px]`} maxLength={200} autoComplete="name" value={form.contactName}
            onChange={(e) => set({ contactName: e.target.value })} aria-invalid={!!shown.contactName} aria-describedby={shown.contactName ? 'wf-contactName-error' : undefined} />
          {fieldError('contactName')}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="wf-email" className={labelClass}>Email</label>
            <input id="wf-email" type="email" className={`${inputClass} min-h-[44px]`} maxLength={320} autoComplete="email" value={form.contactEmail}
              onChange={(e) => set({ contactEmail: e.target.value })} aria-invalid={!!shown.contactEmail} aria-describedby={shown.contactEmail ? 'wf-contactEmail-error' : undefined} />
            {fieldError('contactEmail')}
          </div>
          <div>
            <label htmlFor="wf-phone" className={labelClass}>Phone</label>
            <input id="wf-phone" type="tel" className={`${inputClass} min-h-[44px]`} maxLength={50} autoComplete="tel" value={form.contactPhone}
              onChange={(e) => set({ contactPhone: e.target.value })} />
          </div>
        </div>
        {fieldError('contact')}
        <div>
          <label htmlFor="wf-address" className={labelClass}>Service address</label>
          <input id="wf-address" className={`${inputClass} min-h-[44px]`} maxLength={300} autoComplete="street-address" value={form.serviceAddress}
            onChange={(e) => set({ serviceAddress: e.target.value })} aria-invalid={!!shown.serviceAddress} aria-describedby={shown.serviceAddress ? 'wf-serviceAddress-error' : undefined} />
          {fieldError('serviceAddress')}
        </div>
        <div>
          <label htmlFor="wf-service" className={labelClass}>Service</label>
          <select id="wf-service" className={`${inputClass} min-h-[44px]`} value={form.requestedItemId} onChange={(e) => set({ requestedItemId: e.target.value })}>
            <option value="">Something else (describe below)</option>
            {items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="wf-date" className={labelClass}>Preferred date (optional)</label>
            <input id="wf-date" type="date" min={today} className={`${inputClass} min-h-[44px]`} value={form.preferredDate}
              onChange={(e) => set({ preferredDate: e.target.value })} aria-invalid={!!shown.preferredDate} />
            {fieldError('preferredDate')}
          </div>
          <div>
            <label htmlFor="wf-time" className={labelClass}>Time of day</label>
            <select id="wf-time" className={`${inputClass} min-h-[44px]`} value={form.preferredTimeOfDay}
              onChange={(e) => set({ preferredTimeOfDay: e.target.value as WebFormFields['preferredTimeOfDay'] })}>
              <option value="">No preference</option>
              <option value="MORNING">Morning</option>
              <option value="AFTERNOON">Afternoon</option>
              <option value="EVENING">Evening</option>
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="wf-notes" className={labelClass}>What do you need?</label>
          <textarea id="wf-notes" className={inputClass} rows={4} maxLength={2000} value={form.notes}
            onChange={(e) => set({ notes: e.target.value })} aria-invalid={!!shown.notes} aria-describedby={shown.notes ? 'wf-notes-error' : undefined} />
          {fieldError('notes')}
        </div>
        {/* Honeypot: hidden from people and screen readers; bots that fill it are answered like success and dropped */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
          <label htmlFor="wf-website">Leave this empty</label>
          <input id="wf-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set({ website: e.target.value })} />
        </div>
        {error && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        <button type="submit" className={`${primaryButtonClass} w-full sm:w-auto`} disabled={busy}>{busy ? 'Sending…' : 'Send request'}</button>
      </form>
    </>
  );
}
